import styled, { DefaultTheme } from 'styled-components';
import { Card, Typography, Table, Button, Progress, Alert, Statistic, Badge, Row } from 'antd';

declare module 'styled-components' {
  export interface DefaultTheme {
    mode?: 'light' | 'dark';
  }
}

const getThemeValue = <T,>(props: { theme?: DefaultTheme }, darkValue: T, lightValue: T): T => {
  return props.theme?.mode === 'dark' ? darkValue : lightValue;
};

export const Container = styled.div`
  padding: 16px;
  background: ${(props) => getThemeValue(props, 'transparent', '#f5f5f5')};
  color: ${(props) => getThemeValue(props, '#fff', '#1a1a1a')};

  @media (max-width: 768px) {
    padding: 16px;
  }
`;

export const HeaderSection = styled.div`
  display: flex;
  flex-direction: column;
  gap: 16px;
  margin-bottom: 32px;

  @media (min-width: 768px) {
    flex-direction: row;
    align-items: center;
    justify-content: space-between;
  }
`;

export const HeaderLeft = styled.div`
  display: flex;
  flex-direction: column;
  gap: 8px;
`;

export const HeaderRight = styled.div`
  display: flex;
  align-items: center;
  gap: 12px;
  flex-wrap: wrap;

  @media (max-width: 767px) {
    width: 100%;
    justify-content: flex-start;
  }
`;

export const MainTitle = styled(Typography.Title)`
  margin: 0 !important;
  color: ${(props) => getThemeValue(props, '#fff', '#1a1a1a')} !important;
  font-weight: 700 !important;
  font-size: 28px !important;

  @media (max-width: 768px) {
    font-size: 24px !important;
  }
`;

export const Subtitle = styled(Typography.Text)`
  color: ${(props) => getThemeValue(props, '#8b949e', '#666')} !important;
  font-size: 14px;
  display: block;
`;

export const MetricsGrid = styled(Row)`
  width: 100%;
  gap: 0;
  margin-bottom: 20px;
`;

export const MetricCard = styled(Card)<{ isSelected: boolean; cardColor: string }>`
  border-radius: 12px;
  border: 1px solid
    ${(props) =>
      props.isSelected
        ? props.cardColor
        : getThemeValue(props, '#30363d', '#e5e7eb')};
  background: ${(props) => getThemeValue(props, '#161b22', '#ffffff')};
  box-shadow: ${(props) =>
    props.isSelected
      ? `0 8px 16px ${props.cardColor}40`
      : getThemeValue(
          props,
          '0 2px 8px rgba(0, 0, 0, 0.3)',
          '0 2px 8px rgba(0, 0, 0, 0.08)'
        )};
  transition: all 0.3s cubic-bezier(0.4, 0, 0.2, 1);
  cursor: ${(props) => (props.onClick ? 'pointer' : 'default')};
  height: 100%;

  &:hover {
    transform: translateY(-1px);
    border-color: ${(props) => props.cardColor};
    box-shadow: ${(props) => `0 12px 24px ${props.cardColor}50`};
  }

  .ant-card-body {
    padding: 16px;
    border-radius: 12px;
    background: ${(props) => getThemeValue(props, '#161b22', '#ffffff')};
  }
`;

export const MetricCardHeader = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: flex-start;
  margin-bottom: 16px;
`;

export const MetricCardContent = styled.div`
  display: flex;
  flex-direction: column;
  gap: 8px;
`;

export const MetricTitle = styled(Typography.Text)`
  font-size: 12px;
  color: ${(props) => getThemeValue(props, '#8b949e', '#666')} !important;
  font-weight: 600;
  text-transform: uppercase;
  letter-spacing: 0.5px;
  display: block;
`;

export const MetricValue = styled(Typography.Title)`
  margin: 0 !important;
  color: ${(props) => props.color || getThemeValue(props, '#fff', '#1a1a1a')} !important;
  font-weight: 800 !important;
  font-size: 32px !important;
  line-height: 1.2;

  @media (min-width: 640px) {
    font-size: 36px !important;
  }

  @media (min-width: 768px) {
    font-size: 42px !important;
  }

  @media (min-width: 1024px) {
    font-size: 48px !important;
  }
`;

export const MetricSubtitle = styled(Typography.Text)`
  color: ${(props) => getThemeValue(props, '#8b949e', '#666')} !important;
  font-size: 12px;
  display: block;
`;

export const IconWrapper = styled.div<{ color: string }>`
  display: flex;
  align-items: center;
  justify-content: center;
  width: 48px;
  height: 48px;
  border-radius: 12px;
  background: ${(props) => `${props.color}15`};
  color: ${(props) => props.color};
  font-size: 24px;
  flex-shrink: 0;
  transition: all 0.3s ease;

  @media (max-width: 768px) {
    width: 40px;
    height: 40px;
    font-size: 20px;
  }
`;

export const TrendWrapper = styled.div`
  display: flex;
  align-items: center;
  justify-content: space-between;
  margin-top: 12px;
`;

export const TrendText = styled(Typography.Text)<{ trend: 'up' | 'down' }>`
  color: ${(props) =>
    props.trend === 'up'
      ? '#10b981'
      : '#ef4444'} !important;
  font-size: 12px;
  font-weight: 600;
  display: flex;
  align-items: center;
  gap: 4px;

  svg {
    font-size: 10px;
  }
`;

export const StyledTable = styled(Table)`
  border-radius: 12px;
  overflow: hidden;
  background: ${(props) => getThemeValue(props, '#161b22', '#ffffff')};
  border: 1px solid ${(props) => getThemeValue(props, '#30363d', '#e5e7eb')};

  .ant-table {
    background: ${(props) => getThemeValue(props, '#161b22', '#ffffff')};
    color: ${(props) => getThemeValue(props, '#fff', '#1a1a1a')};
  }

  .ant-table-thead > tr > th {
    background: ${(props) => getThemeValue(props, '#21262d', '#fafafa')} !important;
    color: ${(props) => getThemeValue(props, '#fff', '#1a1a1a')} !important;
    border-bottom: 1px solid ${(props) => getThemeValue(props, '#30363d', '#e5e7eb')} !important;
    font-weight: 600;
    font-size: 12px;
    text-transform: uppercase;
    letter-spacing: 0.5px;
    padding: 12px 16px;

    @media (min-width: 768px) {
      font-size: 13px;
      padding: 16px;
    }
  }

  .ant-table-tbody > tr > td {
    background: ${(props) => getThemeValue(props, '#161b22', '#ffffff')} !important;
    color: ${(props) => getThemeValue(props, '#fff', '#1a1a1a')} !important;
    border-bottom: 1px solid ${(props) => getThemeValue(props, '#21262d', '#e8e8e8')} !important;
    font-size: 13px;
    padding: 12px 16px;

    @media (min-width: 768px) {
      font-size: 14px;
      padding: 16px;
    }
  }

  .ant-table-tbody > tr:hover > td {
    background: ${(props) => getThemeValue(props, '#21262d', '#f5f5f5')} !important;
  }
`;

export const TableCard = styled(Card)`
  border-radius: 16px;
  border: 1px solid ${(props) => getThemeValue(props, '#30363d', '#e5e7eb')};
  background: ${(props) => getThemeValue(props, '#161b22', '#ffffff')};
  box-shadow: ${(props) =>
    getThemeValue(
      props,
      '0 4px 6px -1px rgba(0, 0, 0, 0.3)',
      '0 4px 6px -1px rgba(0, 0, 0, 0.1)'
    )};
  margin-bottom: 24px;

  .ant-card-head {
    border-bottom: 1px solid ${(props) => getThemeValue(props, '#30363d', '#e5e7eb')};
    background: ${(props) => getThemeValue(props, '#161b22', '#ffffff')};
    padding: 20px 24px;

    @media (max-width: 768px) {
      padding: 16px 20px;
    }
  }

  .ant-card-head-title {
    color: ${(props) => getThemeValue(props, '#fff', '#1a1a1a')} !important;
    font-weight: 700;
    font-size: 18px;

    @media (max-width: 768px) {
      font-size: 16px;
    }
  }

  .ant-card-body {
    padding: 24px;
    background: ${(props) => getThemeValue(props, '#161b22', '#ffffff')};

    @media (max-width: 768px) {
      padding: 20px;
    }
  }
`;

export const TableHeader = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: center;
  margin-bottom: 16px;
  flex-wrap: wrap;
  gap: 12px;
`;

export const TableTitle = styled(Typography.Title)`
  margin: 0 !important;
  color: ${(props) => getThemeValue(props, '#fff', '#1a1a1a')} !important;
  font-weight: 700 !important;
  font-size: 18px !important;

  @media (max-width: 768px) {
    font-size: 16px !important;
  }
`;

export const EmptyState = styled.div`
  text-align: center;
  padding: 48px 24px;
  color: ${(props) => getThemeValue(props, '#8b949e', '#666')};
`;

export const EmptyStateText = styled(Typography.Text)`
  color: ${(props) => getThemeValue(props, '#8b949e', '#666')} !important;
  font-size: 14px;
  display: block;
`;

export const ActionButton = styled(Button)`
  border-radius: 8px !important;
  font-weight: 500 !important;
  height: auto;
  padding: 8px 16px;
  background-color: #2D6CCA !important;
  border-color: #2D6CCA !important;
  color: #ffffff !important;

  &:hover {
    background-color: #2563eb !important;
    border-color: #2563eb !important;
    color: #ffffff !important;
  }

  &:focus {
    background-color: #2D6CCA !important;
    border-color: #2D6CCA !important;
    color: #ffffff !important;
  }
`;

export const RefreshButton = styled(ActionButton)`
  background-color: #2D6CCA !important;
  border-color: #2D6CCA !important;
  color: #ffffff !important;

  &:hover {
    background-color: #2563eb !important;
    border-color: #2563eb !important;
    color: #ffffff !important;
  }
`;

export const ExportButton = styled(ActionButton)`
  background-color: #2D6CCA !important;
  border-color: #2D6CCA !important;
  color: #ffffff !important;

  &:hover {
    background-color: #2563eb !important;
    border-color: #2563eb !important;
    color: #ffffff !important;
  }
`;

export const StyledProgress = styled(Progress)`
  min-width: 100px;
  
  .ant-progress-bg {
    background: linear-gradient(90deg, #10b981 0%, #059669 100%);
  }
`;

export const StyledAlert = styled(Alert)`
  border-radius: 12px;
  margin-bottom: 24px;
`;

export const LoadingContainer = styled.div`
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  min-height: 50vh;
  gap: 16px;
`;

export const LoadingText = styled(Typography.Text)`
  color: ${(props) => getThemeValue(props, '#fff', '#1a1a1a')} !important;
  font-weight: 600;
  font-size: 16px;
`;

export const SummaryCard = styled(Card)`
  border-radius: 12px;
  border: 1px solid ${(props) => getThemeValue(props, '#30363d', '#e5e7eb')};
  background: ${(props) => getThemeValue(props, '#161b22', '#ffffff')};
  box-shadow: ${(props) =>
    getThemeValue(
      props,
      '0 4px 6px -1px rgba(0, 0, 0, 0.3)',
      '0 4px 6px -1px rgba(0, 0, 0, 0.1)'
    )};
  margin-bottom: 8px;

  .ant-card-body {
    padding: 16px;
    background: ${(props) => getThemeValue(props, '#161b22', '#ffffff')};

    @media (max-width: 768px) {
      padding: 14px;
    }
  }
`;

export const SummaryGrid = styled(Row)`
  width: 100%;
  margin-bottom: 12px;

  @media (max-width: 768px) {
    .ant-col {
      margin-bottom: 12px;
    }
  }
`;

export const SummaryBox = styled.div`
  text-align: center;
  padding: 20px;
  width: 100%;
  height: 100%;
  min-height: 140px;
  display: flex;
  flex-direction: column;
  justify-content: space-between;
  align-items: center;
  background: ${(props) => getThemeValue(props, '#0d1117', '#f9fafb')};
  border-radius: 12px;
  border: 1px solid ${(props) => getThemeValue(props, '#21262d', '#e5e7eb')};
`;

export const SummaryValue = styled(Typography.Title)`
  margin: 0 0 8px 0 !important;
  color: ${(props) => props.color || getThemeValue(props, '#fff', '#1a1a1a')} !important;
  font-weight: 700 !important;
  font-size: 24px !important;
`;

export const SummaryLabel = styled(Typography.Text)`
  color: ${(props) => getThemeValue(props, '#8b949e', '#666')} !important;
  font-size: 13px;
  display: block;
  margin-bottom: 12px;
`;

export const StyledStatistic = styled(Statistic)`
  .ant-statistic-title {
    color: ${(props) => getThemeValue(props, '#8b949e', '#666')} !important;
    font-size: 12px;
    font-weight: 500;
    margin-bottom: 8px;
  }

  .ant-statistic-content {
    color: ${(props) => getThemeValue(props, '#fff', '#1a1a1a')} !important;
    font-size: 24px;
    font-weight: 700;
    line-height: 1.2;
  }

  .ant-statistic-content-prefix {
    color: ${(props) => getThemeValue(props, '#fff', '#1a1a1a')} !important;
  }
`;

export const StyledBadge = styled(Badge)`
  .ant-badge-status-dot {
    width: 8px;
    height: 8px;
  }
`;

export const DividerStyled = styled.div`
  height: 1px;
  background: ${(props) => getThemeValue(props, '#21262d', '#e5e7eb')};
  margin: 24px 0;

  @media (max-width: 768px) {
    margin: 16px 0;
  }
`;

export const LastUpdatedText = styled(Typography.Text)`
  color: ${(props) => getThemeValue(props, '#8b949e', '#666')} !important;
  font-size: 12px;
  text-align: center;
  display: block;
`;

export const HelpText = styled(Typography.Text)`
  color: ${(props) => getThemeValue(props, '#8b949e', '#666')} !important;
  font-size: 12px;
  display: block;

  &.large {
    font-size: 13px;
  }
`;
